// ═══════════════════════════════════════════════════════════
// نظام ERP - جمعية الدعوة بشمال الرياض
// المكونات المشتركة — تصميم عصري 2026
// ═══════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  X, Plus, Download, CheckCircle, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Search, Target,
  ListChecks, Calendar, FileText, Stamp, Mic, Building2, Wallet, Paperclip, Eye, Receipt, AlertCircle,
  Clock, XCircle, FileSpreadsheet, Upload, Trash2, Loader, ExternalLink, Image as ImageIcon, Edit2,
} from "lucide-react";
import { uploadFile, getSignedUrl, openFile } from "./supabaseClient";
import { useSupaTable, canDelete, currentRole } from "./useSupaTable";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart,
  Pie, Cell,
} from "recharts";
import { C, fmtDate, TEMPLATE_COLORS, EVENT_STAGES, ORG, deptNameOf, normalizeDept, todayISO, canEditSettings } from "./constants";

// ألوان مساعدة
const GOLD = "#dcc160";
const TEAL = "#00b4a2";
const NAVY = "#003d58";

// ═══════════════════════════════════════════════════════════
// لوحة ألوان موحّدة لكل الـ Charts
// ═══════════════════════════════════════════════════════════
export const CHART_PALETTE = [
  "#003d58", "#00b4a2", "#dcc160", "#2D8B59",
  "#7c3aed", "#C4513D", "#e67e22", "#1abc9c",
  "#34495E", "#AD1457", "#6C3483", "#B7950B",
];

// ═══════════════════════════════════════════════════════════
// دوال تحقق مشتركة للنماذج والاستيراد (BUG-042)
// ═══════════════════════════════════════════════════════════
const toLatinDigits = (v) => String(v ?? "").replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d)).replace(/[\s-]/g, "");
// جوال سعودي: 05xxxxxxxx أو +9665xxxxxxxx أو 9665xxxxxxxx — والأرضي 01xxxxxxxx
export const isValidPhone = (v) => /^(?:\+?966|0)(?:5\d{8}|1\d{8})$/.test(toLatinDigits(v));
export const isValidEmail = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v || "").trim());
// رقم الهوية/الإقامة: 10 أرقام تبدأ بـ 1 (مواطن) أو 2 (مقيم)
export const isValidNationalId = (v) => /^[12]\d{9}$/.test(toLatinDigits(v));
export const isPositiveAmount = (v) => v !== "" && v !== null && !isNaN(Number(v)) && Number(v) > 0;
// تاريخ النهاية لا يسبق البداية (أي واحد فاضي = مقبول)
export const isValidRange = (from, to) => !from || !to || String(to) >= String(from);

// ═══════════════════════════════════════════════════════════
// Tooltip موحّد للـ Charts
// ═══════════════════════════════════════════════════════════
export const BrandTooltip = ({ active, payload, totalValue, suffix = "" }) => {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0];
  const value = Number(item.value || 0);
  const name = item.name || item.payload?.name || "";
  const color = item.color || item.payload?.fill || NAVY;
  const pct = totalValue > 0 ? Math.round((value / totalValue) * 100) : null;

  return (
    <div style={{
      background: "#fff", borderRadius: 8, padding: "10px 14px",
      boxShadow: "0 8px 24px rgba(0,20,35,0.12)",
      border: `1px solid rgba(0,20,35,0.06)`,
      minWidth: 140, fontFamily: "inherit",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: "inline-block" }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: NAVY }}>{name}</span>
      </div>
      <div style={{ fontSize: 11, color: C.text }}>
        القيمة: <strong>{value.toLocaleString("en-US")}{suffix}</strong>
      </div>
      {pct !== null && (
        <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
          النسبة: <strong style={{ color: TEAL }}>{pct}%</strong>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// Legend موحّد للـ PieCharts — مفتاح خارجي عمودي
// ═══════════════════════════════════════════════════════════
export const BrandLegend = ({ data = [], colors = CHART_PALETTE, totalValue, valueFormatter = (v) => v.toLocaleString("en-US") }) => {
  const total = totalValue || data.reduce((s, d) => s + (Number(d.value) || 0), 0);
  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 6,
      maxHeight: 240, overflowY: "auto", padding: "4px 8px",
    }}>
      {data.map((d, i) => {
        const color = d.color || colors[i % colors.length];
        const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
        return (
          <div key={d.name + i} style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "5px 8px", borderRadius: 6,
            fontSize: 11,
          }}>
            <span style={{
              width: 12, height: 12, borderRadius: 3, background: color, flexShrink: 0,
            }} />
            <span style={{ flex: 1, color: C.text, fontWeight: 500 }}>{d.name}</span>
            <span style={{ color: C.textLight, fontWeight: 600 }}>{valueFormatter(d.value)}</span>
            <span style={{ color: TEAL, fontWeight: 700, fontSize: 10, minWidth: 32, textAlign: "left" }}>{pct}%</span>
          </div>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// BrandPie — Donut Chart بدون labels داخلية + legend خارجي
// ═══════════════════════════════════════════════════════════
export const BrandPie = ({ data = [], colors = CHART_PALETTE, height = 240, valueFormatter, suffix = "" }) => {
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, alignItems: "center" }}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name"
            cx="50%" cy="50%"
            innerRadius="60%" outerRadius="92%"
            paddingAngle={2}
            stroke="#fff" strokeWidth={2}
            animationDuration={900} animationBegin={0}
            label={false} labelLine={false}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.color || colors[i % colors.length]} />
            ))}
          </Pie>
          <Tooltip content={<BrandTooltip totalValue={total} suffix={suffix} />} />
        </PieChart>
      </ResponsiveContainer>
      <BrandLegend data={data} colors={colors} totalValue={total} valueFormatter={valueFormatter} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 1. Modal
// ═══════════════════════════════════════════════════════════
export const Modal = ({ open, onClose, title, children, size = "md", footer }) => {
  if (!open) return null;
  const widths = { sm: 400, md: 560, lg: 760, xl: 960 };
  // النقر على الخلفية لا يغلق النافذة — حتى لا تضيع البيانات المدخلة بنقرة خطأ (BUG-041)
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,20,35,0.45)",
      backdropFilter: "blur(4px)", display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 1000, padding: 20,
      animation: "fadeIn 0.18s ease",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, width: "100%",
        maxWidth: widths[size], maxHeight: "90vh", display: "flex",
        flexDirection: "column", boxShadow: "0 24px 64px rgba(0,20,35,0.2)",
        animation: "modalIn 0.22s cubic-bezier(0.16,1,0.3,1)",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "18px 22px", borderBottom: `1px solid ${C.border}`,
        }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{title}</h3>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            color: C.textLight, padding: 4, borderRadius: 8,
          }}><X size={18} /></button>
        </div>
        <div style={{ padding: "18px 22px", overflowY: "auto", flex: 1 }}>{children}</div>
        {footer && <div style={{
          padding: "14px 22px", borderTop: `1px solid ${C.border}`,
          display: "flex", justifyContent: "flex-start", gap: 8,
        }}>{footer}</div>}
      </div>
      <style>{`
        @keyframes modalIn { from { opacity:0; transform:scale(0.95) translateY(8px); } to { opacity:1; transform:scale(1) translateY(0); } }
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 2. Field
// ═══════════════════════════════════════════════════════════
export const Field = ({ label, type = "text", value, onChange, options, required, placeholder, rows = 3, hint }) => {
  const base = {
    width: "100%", padding: "10px 12px", border: `1.5px solid ${C.border}`,
    borderRadius: 10, fontSize: 13, fontFamily: "inherit", color: C.text,
    background: "#fff", outline: "none", transition: "all 0.2s",
  };

  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.textLight, marginBottom: 6 }}>
          {label} {required && <span style={{ color: C.danger }}>*</span>}
        </label>
      )}
      {type === "textarea" ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} rows={rows} style={{ ...base, resize: "vertical" }}
          onFocus={(e) => { e.target.style.borderColor = GOLD; e.target.style.boxShadow = `0 0 0 3px ${GOLD}25`; }}
          onBlur={(e) => { e.target.style.borderColor = C.border; e.target.style.boxShadow = "none"; }} />
      ) : type === "select" ? (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} style={{ ...base, cursor: "pointer" }}
          onFocus={(e) => { e.target.style.borderColor = GOLD; e.target.style.boxShadow = `0 0 0 3px ${GOLD}25`; }}
          onBlur={(e) => { e.target.style.borderColor = C.border; e.target.style.boxShadow = "none"; }}>
          <option value="">— اختر —</option>
          {(options || []).map((o, i) => {
            const val = typeof o === "object" ? o.value : o;
            const lab = typeof o === "object" ? o.label : o;
            return <option key={i} value={val}>{lab}</option>;
          })}
        </select>
      ) : (
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} style={base}
          onFocus={(e) => { e.target.style.borderColor = GOLD; e.target.style.boxShadow = `0 0 0 3px ${GOLD}25`; }}
          onBlur={(e) => { e.target.style.borderColor = C.border; e.target.style.boxShadow = "none"; }} />
      )}
      {hint && <div style={{ fontSize: 10, color: C.textLight, marginTop: 4 }}>{hint}</div>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 3. FormActions
// ═══════════════════════════════════════════════════════════
export const FormActions = ({ onCancel, onSave, saveLabel = "حفظ", cancelLabel = "إلغاء", saving }) => (
  <div style={{ display: "flex", gap: 8 }}>
    <Btn onClick={onSave} disabled={saving}>{saving ? "جاري الحفظ..." : saveLabel}</Btn>
    <Btn variant="ghost" onClick={onCancel}>{cancelLabel}</Btn>
  </div>
);

// ═══════════════════════════════════════════════════════════
// 4. Btn — مع تدريجات وhover
// ═══════════════════════════════════════════════════════════
export const Btn = ({ children, variant = "primary", size = "md", onClick, disabled, style: cs, ...props }) => {
  const sizes = {
    sm: { padding: "5px 12px", fontSize: 11 },
    md: { padding: "8px 18px", fontSize: 12 },
    lg: { padding: "12px 24px", fontSize: 14 },
  };
  const variants = {
    primary: { background: `linear-gradient(135deg, ${NAVY} 0%, #00526e 100%)`, color: "#fff", border: "none", shadowHover: "0 4px 15px rgba(0,61,88,0.25)" },
    accent: { background: `linear-gradient(135deg, ${GOLD} 0%, #c4a830 100%)`, color: NAVY, border: "none", shadowHover: "0 4px 15px rgba(220,193,96,0.35)" },
    success: { background: C.success, color: "#fff", border: "none", shadowHover: "0 4px 15px rgba(45,139,89,0.25)" },
    // تيل أغمق لتباين النص الأبيض (BUG-065)
    teal: { background: "linear-gradient(135deg, #007a6e 0%, #00695f 100%)", color: "#fff", border: "none", shadowHover: "0 4px 15px rgba(0,180,162,0.25)" },
    outline: { background: "transparent", color: NAVY, border: `1.5px solid ${C.border}`, shadowHover: "0 2px 8px rgba(0,61,88,0.1)" },
    ghost: { background: "transparent", color: C.textLight, border: "none", shadowHover: "none" },
    danger: { background: C.danger, color: "#fff", border: "none", shadowHover: "0 4px 15px rgba(196,81,61,0.25)" },
  };
  const v = variants[variant] || variants.primary;
  const s = sizes[size] || sizes.md;
  const [hover, setHover] = useState(false);

  return (
    <button onClick={onClick} disabled={disabled} {...props}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...s,
        background: v.background, color: v.color, border: v.border,
        borderRadius: 10, fontWeight: 600, fontFamily: "inherit",
        cursor: disabled ? "not-allowed" : "pointer", display: "inline-flex",
        alignItems: "center", gap: 6,
        transition: "all 0.2s ease",
        opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap",
        boxShadow: hover && !disabled ? v.shadowHover : "none",
        transform: hover && !disabled ? "translateY(-1px)" : "translateY(0)",
        ...cs,
      }}>{children}</button>
  );
};

// ═══════════════════════════════════════════════════════════
// 5. Toast
// ═══════════════════════════════════════════════════════════
// رسائل الخطأ/التحقق تظهر كخطأ حتى لو الشاشة مررت type="success" (BUG-063)
const ERROR_HINT = /(خطأ|تعذّر|تعذر|الرجاء|يرجى|غير صالح|غير صحيح|لا يمكن|لا تملك|يجب|لا يكفي|لا تحتوي|مسجل مسبقاً|يوجد)/;
export const Toast = ({ msg, type = "success", onClose }) => {
  useEffect(() => { if (msg) { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); } }, [msg, onClose]);
  if (!msg) return null;
  const kind = type === "success" && ERROR_HINT.test(String(msg)) ? "error" : type;
  const colors = { success: C.success, error: C.danger, warning: C.warning, info: C.primary };
  const color = colors[kind] || C.success;
  const Icon = kind === "success" ? CheckCircle : AlertCircle;
  return (
    <div style={{
      position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
      background: "#fff", color: C.text, padding: "12px 20px", borderRadius: 12,
      boxShadow: "0 8px 32px rgba(0,20,35,0.12)", fontSize: 13, fontWeight: 500,
      display: "flex", alignItems: "center", gap: 10, zIndex: 2000,
      borderRight: `4px solid ${color}`, animation: "toastIn 0.3s ease",
    }}>
      <Icon size={16} style={{ color }} />
      {msg}
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.textLight, padding: 0, marginRight: 4 }}>
        <X size={14} />
      </button>
      <style>{`@keyframes toastIn { from { opacity:0; transform:translateX(-50%) translateY(20px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 5b. GlobalErrorToast — يعرض أي خطأ حفظ/قراءة من Supabase (notifyError)
// ═══════════════════════════════════════════════════════════
export const GlobalErrorToast = () => {
  const [msg, setMsg] = useState("");
  useEffect(() => {
    const onErr = (e) => setMsg(e.detail || "حدث خطأ غير متوقع");
    window.addEventListener("erp:error", onErr);
    return () => window.removeEventListener("erp:error", onErr);
  }, []);
  useEffect(() => { if (msg) { const t = setTimeout(() => setMsg(""), 8000); return () => clearTimeout(t); } }, [msg]);
  if (!msg) return null;
  return (
    <div role="alert" style={{
      position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
      background: "#fff", color: C.text, padding: "12px 18px", borderRadius: 12,
      boxShadow: "0 8px 32px rgba(0,20,35,0.18)", fontSize: 13, fontWeight: 500,
      display: "flex", alignItems: "flex-start", gap: 10, zIndex: 3000, maxWidth: "min(560px, 92vw)",
      borderRight: `4px solid ${C.danger}`, lineHeight: 1.6,
    }}>
      <AlertCircle size={18} style={{ color: C.danger, flexShrink: 0, marginTop: 2 }} />
      <div>{msg}</div>
      <button onClick={() => setMsg("")} style={{ background: "none", border: "none", cursor: "pointer", color: C.textLight, padding: 0 }}>
        <X size={14} />
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 5c. ErrorBoundary — أي خطأ أثناء رسم موديول يظهر كرسالة بدل شاشة بيضاء (BUG-022)
// ═══════════════════════════════════════════════════════════
export class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("[ERP] render error:", error, info?.componentStack); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div role="alert" style={{ maxWidth: 480, margin: "60px auto", background: "#fff", borderRadius: 12, padding: 28, textAlign: "center", border: `1px solid ${C.border}` }}>
        <AlertCircle size={32} style={{ color: C.danger }} />
        <h3 style={{ color: C.text, margin: "12px 0 6px", fontSize: 16 }}>حدث خطأ أثناء عرض هذه الصفحة</h3>
        <p style={{ fontSize: 12, color: C.textLight, margin: "0 0 16px", lineHeight: 1.7 }}>
          قد يكون أحد السجلات يحتوي على قيمة غير متوقعة. باقي النظام يعمل بشكل طبيعي.
        </p>
        <Btn onClick={() => this.setState({ error: null })}>إعادة المحاولة</Btn>
      </div>
    );
  }
}

// ═══════════════════════════════════════════════════════════
// 6. Card — مع hover
// ═══════════════════════════════════════════════════════════
export const Card = ({ children, title, actions, padding = 20, style, hoverable = true }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => hoverable && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: "#fff", borderRadius: 14, padding,
        boxShadow: hover
          ? "0 8px 25px rgba(0,61,88,0.08)"
          : "0 1px 3px rgba(0,20,35,0.04), 0 1px 2px rgba(0,20,35,0.02)",
        transition: "box-shadow 0.3s ease",
        ...style,
      }}>
      {(title || actions) && (
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 14, paddingBottom: title ? 10 : 0,
          borderBottom: title ? `1px solid ${C.border}` : "none",
        }}>
          {title && <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>{title}</h3>}
          {actions && <div style={{ display: "flex", gap: 6 }}>{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 7. KPI — مع borderTop ملوّن وhover
// ═══════════════════════════════════════════════════════════
export const KPI = ({ label, value, sub, color = NAVY, icon, trend }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: "#fff", borderRadius: 14, padding: "16px 18px",
        boxShadow: hover ? "0 6px 20px rgba(0,61,88,0.1)" : "0 1px 3px rgba(0,20,35,0.04)",
        display: "flex", alignItems: "flex-start", gap: 12,
        borderTop: `3px solid ${color}`,
        transition: "all 0.25s ease",
        transform: hover ? "translateY(-2px)" : "translateY(0)",
      }}>
      {icon && (
        <div style={{
          width: 38, height: 38, borderRadius: 10,
          background: `linear-gradient(135deg, ${color}18, ${color}08)`,
          color,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>{icon}</div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: C.textLight, fontWeight: 500, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1.1 }}>{value}</div>
        {sub && (
          <div style={{ fontSize: 11, color: trend === "up" ? C.success : trend === "down" ? C.danger : C.textLight, marginTop: 4, fontWeight: 500, display: "flex", alignItems: "center", gap: 3 }}>
            {trend === "up" && <TrendingUp size={12} />}
            {trend === "down" && <TrendingDown size={12} />}
            {sub}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 8. Badge — مع hover خفيف
// ═══════════════════════════════════════════════════════════
// ألوان فاتحة تُستخدم كنص على أبيض → نسخة أغمق للنص فقط (BUG-065)
const BADGE_TEXT = { "#d4a843": "#8A6D1F", "#dcc160": "#7A6A24", "#00b4a2": "#007A6E" };
// لون مختصر (#fff) → كامل، حتى تعمل الشفافية (#ffffff12) — BUG-064
const fullHex = (c) => (/^#[0-9a-f]{3}$/i.test(c || "") ? `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}` : (c || NAVY));
export const Badge = ({ children, color = NAVY, gold }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", padding: "3px 10px",
    borderRadius: 20, fontSize: 10, fontWeight: 600,
    background: gold ? `linear-gradient(135deg, ${GOLD}, #c4a830)` : `${fullHex(color)}12`,
    color: gold ? NAVY : (BADGE_TEXT[fullHex(color).toLowerCase()] || fullHex(color)),
    lineHeight: 1.4, whiteSpace: "nowrap",
    transition: "opacity 0.2s",
    cursor: "default",
  }}>{children}</span>
);

// ═══════════════════════════════════════════════════════════
// 9. Tabs — مع hover
// ═══════════════════════════════════════════════════════════
export const Tabs = ({ tabs, active, onChange }) => (
  <div style={{
    display: "flex", gap: 2, padding: 3, background: "#f0f2f5",
    borderRadius: 12, marginBottom: 18, overflowX: "auto",
    scrollbarWidth: "none",
  }}>
    {tabs.map((t) => {
      const isActive = active === t.id;
      return (
        <button key={t.id} onClick={() => onChange(t.id)}
          onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.5)"; }}
          onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
          style={{
            padding: "8px 14px", borderRadius: 10, border: "none",
            fontSize: 11, fontFamily: "inherit", fontWeight: isActive ? 600 : 400,
            cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.2s",
            background: isActive ? "#fff" : "transparent",
            color: isActive ? NAVY : C.textLight,
            boxShadow: isActive ? "0 1px 3px rgba(0,20,35,0.08)" : "none",
            display: "flex", alignItems: "center", gap: 5,
          }}>
          {t.icon}
          {t.label}
        </button>
      );
    })}
    <style>{`div::-webkit-scrollbar { display: none; }`}</style>
  </div>
);

// ═══════════════════════════════════════════════════════════
// 10. DataTable
// ═══════════════════════════════════════════════════════════
export const DataTable = ({ columns, data, onRowClick, emptyMsg = "لا توجد بيانات" }) => (
  <div style={{ overflowX: "auto", borderRadius: 12, background: "#fff" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col.key} style={{
              padding: "10px 14px", textAlign: "right", fontWeight: 600,
              color: C.textLight, fontSize: 11, background: "#f8f9fb",
              borderBottom: `1px solid ${C.border}`,
            }}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.length === 0 ? (
          <tr><td colSpan={columns.length} style={{ padding: 40, textAlign: "center", color: C.textLight }}>{emptyMsg}</td></tr>
        ) : data.map((row, i) => (
          <tr key={row.id || i}
            onClick={() => onRowClick?.(row)}
            style={{
              cursor: onRowClick ? "pointer" : "default",
              transition: "background 0.15s",
              borderBottom: `1px solid ${C.border}`,
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#f8f9fb"}
            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
            {columns.map((col) => (
              <td key={col.key} style={{ padding: "11px 14px", color: C.text }}>
                {col.render ? col.render(row) : row[col.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ═══════════════════════════════════════════════════════════
// 11. exportCSV
// ═══════════════════════════════════════════════════════════
// تحميل عنصر من الصفحة كصورة PNG (html-to-image بيتحمّل عند الحاجة بس)
export const downloadPNG = async (node, filename) => {
  if (!node) return;
  const { toPng } = await import("html-to-image");
  const url = await toPng(node, { pixelRatio: 2, backgroundColor: "#fff", cacheBust: true });
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.png`;
  a.click();
};

export const exportCSV = (filename, rows, allColumns) => {
  const columns = allColumns.filter(c => c.key !== "_delete" && c.key !== "actions");
  const header = columns.map(c => c.label).join(",");
  const body = rows.map(r =>
    columns.map(c => {
      let val = String(c.csvValue ? c.csvValue(r) : (r[c.key] ?? ""));
      // حماية من حقن معادلات Excel: نص يبدأ بـ = + - @ يُسبق بـ ' (الأرقام السالبة تبقى كما هي) — BUG-051
      if (/^[=+\-@\t\r]/.test(val) && isNaN(Number(val))) val = `'${val}`;
      return `"${val.replace(/"/g, '""')}"`;
    }).join(",")
  ).join("\n");
  const bom = "\uFEFF";
  const blob = new Blob([bom + header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${filename}-${todayISO()}.csv`;
  a.click(); URL.revokeObjectURL(url);
};

// ═══════════════════════════════════════════════════════════
// 12. ProgressBar — مع تدريج
// ═══════════════════════════════════════════════════════════
export const ProgressBar = ({ value = 0, color, height = 6 }) => (
  <div style={{ height, background: "#f0f2f5", borderRadius: 4, overflow: "hidden" }}>
    <div style={{
      width: `${Math.min(100, Math.max(0, value))}%`,
      height: "100%",
      background: color || `linear-gradient(to left, ${TEAL}, ${NAVY})`,
      borderRadius: 4, transition: "width 0.5s ease",
    }} />
  </div>
);

// ═══════════════════════════════════════════════════════════
// 13. ReportsTab
// ═══════════════════════════════════════════════════════════
export const ReportsTab = ({ kpis = [], chartData = [], chartType = "bar", columns = [], data = [], exportName = "report" }) => {
  const ChartComp = chartType === "line" ? LineChart : BarChart;
  const DataComp = chartType === "line" ? Line : Bar;
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 18 }}>
        {kpis.map((k, i) => <KPI key={i} label={k.label} value={k.value} color={k.color} />)}
      </div>
      {chartData.length > 0 && (
        <Card title="التقرير البياني" style={{ marginBottom: 18 }}>
          <ResponsiveContainer width="100%" height={240}>
            <ChartComp data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <DataComp type="monotone" dataKey="value" fill={NAVY} stroke={NAVY} strokeWidth={2} radius={chartType === "bar" ? [6, 6, 0, 0] : undefined} />
            </ChartComp>
          </ResponsiveContainer>
        </Card>
      )}
      <Card title="البيانات التفصيلية" actions={
        <Btn variant="outline" size="sm" onClick={() => exportCSV(exportName, data, columns)}>
          <Download size={12} /> تصدير CSV
        </Btn>
      }>
        <DataTable columns={columns} data={data} />
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 14. MeetingsTab — محسّن بمحاضر وقرارات
// ═══════════════════════════════════════════════════════════
export const MeetingsTab = ({ moduleName = "الإدارة", initialMeetings }) => {
  const meetingsApi = useSupaTable("module_meetings", { scope: { module: moduleName }, order: "meeting_date.desc.nullslast,id.desc" });
  const { rows: meetings, insert } = meetingsApi;
  const [editing, setEditing] = useState(null); // تعديل/إغلاق الاجتماع (BUG-047)
  const saveEdit = async (v) => {
    // كل سطر = قرار؛ القرارات الموجودة بنفس النص تحتفظ بمسؤولها وحالتها
    const old = editing.decisions || [];
    const decisions = (v.decisions || []).map((text, i) => old.find(d => d.text === text) || { id: Date.now() + i, text, responsible: "", deadline: "", status: "جاري" });
    const saved = await meetingsApi.update(editing.id, { ...v, decisions });
    if (saved) setToast(v.status === "completed" ? "تم تسجيل انعقاد الاجتماع" : "تم تحديث الاجتماع");
    return !!saved;
  };
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [toast, setToast] = useState("");
  const [form, setForm] = useState({ title: "", date: "", time: "", type: "دوري", agenda: "", minutes: "", attendeesList: "", absent: "", attachment: "" });

  const save = async () => {
    if (!form.title || !form.date) { setToast("الرجاء إدخال العنوان والتاريخ"); return; }
    setSaving(true);
    const saved = await insert({
      ...form,
      attendeesList: form.attendeesList.split(/[,،]/).map(s => s.trim()).filter(Boolean),
      absent: form.absent.split(/[,،]/).map(s => s.trim()).filter(Boolean),
      attachment: form.attachment || null,
      status: "scheduled",
      decisions: [],
    });
    setSaving(false);
    if (!saved) return;
    setModal(false);
    setForm({ title: "", date: "", time: "", type: "دوري", agenda: "", minutes: "", attendeesList: "", absent: "", attachment: "" });
    setToast("تم حفظ الاجتماع بنجاح");
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: C.textLight }}>إجمالي: {meetings.length} اجتماع</div>
        <Btn onClick={() => setModal(true)}><Plus size={14} /> جدولة اجتماع</Btn>
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        {meetings.map(m => (
          <Card key={m.id} padding={14}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
              onClick={() => setExpanded(expanded === m.id ? null : m.id)}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{m.title}</span>
                  <Badge color={m.type === "دوري" ? TEAL : GOLD}>{m.type}</Badge>
                </div>
                <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
                  {fmtDate(m.date)} | {m.time} | {m.attendeesList?.length || 0} حضور
                  {m.absent?.length > 0 && ` | ${m.absent.length} غياب`}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Badge color={m.status === "completed" ? C.success : NAVY}>
                  {m.status === "completed" ? "منعقد" : "مجدول"}
                </Badge>
                <Btn size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setEditing(m); }}>
                  {m.status === "completed" ? "تعديل" : "تسجيل الانعقاد"}
                </Btn>
                <DeleteBtn what={m.title} onDelete={() => meetingsApi.remove(m.id)} />
                {expanded === m.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
            </div>

            {expanded === m.id && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                {m.attendeesList?.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, marginBottom: 4 }}>الحضور:</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {m.attendeesList.map((a, i) => <Badge key={i} color={C.success}>{a}</Badge>)}
                    </div>
                  </div>
                )}
                {m.absent?.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, marginBottom: 4 }}>الغياب:</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {m.absent.map((a, i) => <Badge key={i} color={C.danger}>{a}</Badge>)}
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, marginBottom: 4 }}>جدول الأعمال:</div>
                  <div style={{ fontSize: 12, color: C.text, whiteSpace: "pre-wrap", background: "#f8f9fb", padding: 10, borderRadius: 8 }}>
                    {m.agenda || "—"}
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, marginBottom: 4 }}>محضر الاجتماع:</div>
                  <div style={{ fontSize: 12, color: C.text, whiteSpace: "pre-wrap", background: "#f8f9fb", padding: 10, borderRadius: 8, minHeight: 40 }}>
                    {m.minutes || "لم يتم تدوين المحضر بعد"}
                  </div>
                </div>
                {m.attachment && (() => {
                  const att = typeof m.attachment === "string" ? { path: m.attachment, name: m.attachment } : m.attachment;
                  return (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, marginBottom: 4 }}>مرفق المحضر:</div>
                      <button type="button"
                        onClick={(e) => { e.preventDefault(); openFile(att.path); }}
                        style={{ cursor: "pointer", fontFamily: "inherit",
                          display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px",
                          background: `${TEAL}10`, border: `1px solid ${TEAL}30`, borderRadius: 8,
                          fontSize: 12, color: NAVY, fontWeight: 500, textDecoration: "none",
                        }}>
                        <FileText size={14} style={{ color: TEAL }} />
                        {att.name}
                        <ExternalLink size={11} style={{ color: TEAL }} />
                      </button>
                    </div>
                  );
                })()}
                {m.decisions?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, marginBottom: 6 }}>القرارات:</div>
                    {m.decisions.map(d => (
                      <div key={d.id} style={{ background: "#f8f9fb", padding: 10, borderRadius: 8, marginBottom: 6 }}>
                        <div style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>{d.text}</div>
                        <div style={{ fontSize: 10, color: C.textLight, marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap" }}>
                          <span>{d.responsible}</span>
                          <span>{fmtDate(d.deadline)}</span>
                          <Badge color={d.status === "مكتمل" ? C.success : d.status === "متأخر" ? C.danger : C.warning}>{d.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        ))}
      </div>

      <RecordFormModal open={!!editing} onClose={() => setEditing(null)} title={`الاجتماع — ${editing?.title || ""}`}
        fields={[
          { key: "status", label: "حالة الاجتماع", type: "select", required: true, options: [{ value: "scheduled", label: "مجدول" }, { value: "completed", label: "منعقد" }] },
          { key: "date", label: "التاريخ", type: "date", required: true },
          { key: "attendeesList", label: "الحضور", type: "list" },
          { key: "absent", label: "الغياب", type: "list" },
          { key: "minutes", label: "محضر الاجتماع", type: "textarea" },
          { key: "decisions", label: "القرارات", type: "list", lines: true, hint: "قرار في كل سطر" },
        ]}
        initial={editing ? { ...editing, decisions: (editing.decisions || []).map(d => d.text) } : {}}
        onSubmit={saveEdit} />

      <Modal open={modal} onClose={() => setModal(false)} title="جدولة اجتماع جديد" size="lg"
        footer={<FormActions onCancel={() => setModal(false)} onSave={save} saving={saving} />}>
        <Field label="عنوان الاجتماع" required value={form.title} onChange={v => setForm({ ...form, title: v })} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
          <Field label="الوقت" type="time" value={form.time} onChange={v => setForm({ ...form, time: v })} />
          <Field label="نوع الاجتماع" type="select" value={form.type} onChange={v => setForm({ ...form, type: v })}
            options={["دوري", "غير دوري"]} />
        </div>
        <Field label="الحضور (مفصولة بفواصل)" value={form.attendeesList} onChange={v => setForm({ ...form, attendeesList: v })}
          placeholder="أ. عبدالله, أ. محمد..." />
        <Field label="الغياب (مفصولة بفواصل)" value={form.absent} onChange={v => setForm({ ...form, absent: v })} />
        <Field label="جدول الأعمال" type="textarea" rows={3} value={form.agenda} onChange={v => setForm({ ...form, agenda: v })} />
        <Field label="محضر الاجتماع" type="textarea" rows={5} value={form.minutes} onChange={v => setForm({ ...form, minutes: v })} />
        <FileUploadField
          label="مرفق المحضر"
          value={form.attachment}
          onChange={v => setForm({ ...form, attachment: v })}
          folder="meetings"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          maxSizeMB={10}
        />
      </Modal>

      <Toast msg={toast} onClose={() => setToast("")} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 15b. OperationalPlanTab — الخطة التشغيلية من جدول operational_plan_programs (BUG-050)
// الإضافة والتعديل والحذف لـ exec / finance / system بس (SETTINGS_EDITORS — migration 014)
// ═══════════════════════════════════════════════════════════
const PLAN_FIELDS = [
  { key: "name", label: "اسم البرنامج", required: true, placeholder: "مثال: حملة دعوية شاملة" },
  { key: "cost", label: "التكلفة (ر.س)", type: "number", required: true, min: 0 },
  { key: "goal", label: "الهدف من البرنامج", placeholder: "ما الهدف الرئيسي؟" },
  { key: "description", label: "وصف البرنامج", type: "textarea" },
  { key: "kpis", label: "المؤشرات والمستهدفات", type: "list", lines: true, hint: "ضع كل مؤشر في سطر مستقل" },
];

export const OperationalPlanTab = ({ departmentKey }) => {
  const plansApi = useSupaTable("operational_plan_programs", { scope: { department: departmentKey || "" }, order: "id.asc", skip: !departmentKey });
  const programs = plansApi.rows;
  const editable = canEditSettings(currentRole());
  const [editing, setEditing] = useState(null);

  if (!departmentKey) return (
    <div style={{ padding: 24, textAlign: "center", color: C.textLight, fontSize: 13 }}>
      لا توجد بيانات خطة تشغيلية لهذه الإدارة
    </div>
  );

  const total = programs.reduce((s, p) => s + (Number(p.cost) || 0), 0);
  const totalKPIs = programs.reduce((s, p) => s + (p.kpis?.length || 0), 0);

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 16 }}>
        <KPI label="البرامج" value={programs.length} color={NAVY} icon={<Target size={16} />} />
        <KPI label="الميزانية الكلية" value={`${(total / 1000).toFixed(0)} ألف`} color={GOLD} icon={<ListChecks size={16} />} />
        <KPI label="المؤشرات" value={totalKPIs} color={TEAL} icon={<Target size={16} />} />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 12, background: `${NAVY}08`, borderRadius: 10, marginBottom: 14, border: `1px solid ${NAVY}20`, gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>{deptNameOf(departmentKey)} — الخطة التشغيلية {new Date().getFullYear()}</div>
          <div style={{ fontSize: 12, color: C.textLight, marginTop: 4 }}>
            إجمالي الميزانية: <strong style={{ color: NAVY }}>{total.toLocaleString("en-US")} ر.س</strong> | {programs.length} برنامج | {totalKPIs} مؤشر
          </div>
        </div>
        {editable && (
          <AddRecordBtn api={plansApi} variant="accent" label="إعداد برنامج جديد" title="إعداد برنامج جديد في الخطة التشغيلية"
            fields={PLAN_FIELDS} />
        )}
      </div>

      {programs.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: C.textLight, fontSize: 13 }}>
          {plansApi.loading ? "جاري التحميل..." : "لم تُسجل برامج في الخطة التشغيلية لهذه الإدارة"}
        </div>
      )}
      <div style={{ display: "grid", gap: 10 }}>
        {programs.map((prog, i) => {
          const pctBudget = total > 0 ? Math.round((prog.cost / total) * 100) : 0;
          return (
            <Card key={prog.id} padding={14}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{
                      width: 28, height: 28, borderRadius: 8, background: NAVY, color: "#fff",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 800, flexShrink: 0,
                    }}>{i + 1}</span>
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>{prog.name}</h4>
                    <Badge color={GOLD}>{Number(prog.cost || 0).toLocaleString("en-US")} ر.س</Badge>
                    <Badge color={TEAL}>{pctBudget}% من الميزانية</Badge>
                    {editable && (
                      <button type="button" title="تعديل البرنامج" onClick={() => setEditing(prog)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: NAVY, padding: 2, display: "inline-flex" }}>
                        <Edit2 size={13} />
                      </button>
                    )}
                    {editable && <DeleteBtn what={prog.name} onDelete={() => plansApi.remove(prog.id)} />}
                  </div>
                  {prog.description && (
                    <div style={{ fontSize: 12, color: C.text, marginTop: 6, lineHeight: 1.7, padding: "6px 10px", background: "#f8f9fb", borderRadius: 8 }}>
                      {prog.description}
                    </div>
                  )}
                  {prog.goal && (
                    <div style={{ fontSize: 12, color: C.textLight, marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                      <Target size={12} style={{ color: GOLD }} />
                      <strong style={{ color: NAVY }}>الهدف:</strong> {prog.goal}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
                  <ListChecks size={12} /> المؤشرات والمستهدفات
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {(prog.kpis || []).map((kpi, ki) => (
                    <div key={ki} style={{
                      padding: "5px 10px", background: `${TEAL}08`, border: `1px solid ${TEAL}25`,
                      borderRadius: 6, fontSize: 11, color: C.text, display: "flex", alignItems: "center", gap: 4,
                    }}>
                      <CheckCircle size={11} style={{ color: TEAL }} />
                      {kpi}
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <RecordFormModal open={!!editing} onClose={() => setEditing(null)} title="تعديل برنامج في الخطة التشغيلية"
        fields={PLAN_FIELDS} initial={editing || {}}
        onSubmit={async (values) => !!(await plansApi.update(editing.id, values))} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 16. TransactionsTab — الشؤون المالية (المعاملات المالية)
// ═══════════════════════════════════════════════════════════
const TX_TYPES = [
  "مصروف نثري", "فاتورة مورد", "طلب شراء", "مصروف تشغيلي",
  "رواتب وأجور", "صيانة ومعدات", "إيجار", "مواصلات",
  "ضيافة", "مطبوعات", "أخرى",
];

const TX_STATUSES = {
  paid: { name: "مدفوع", color: "#2D8B59", icon: <CheckCircle size={11} /> },
  pending: { name: "معلق", color: "#dcc160", icon: <Clock size={11} /> },
  approval: { name: "قيد الاعتماد", color: "#00b4a2", icon: <AlertCircle size={11} /> },
  rejected: { name: "مرفوض", color: "#C4513D", icon: <XCircle size={11} /> },
};

const TX_PAYMENT_METHODS = ["نقدي", "تحويل بنكي", "شيك", "بطاقة ائتمانية"];

const fmtSAR = (n) => `${Number(n || 0).toLocaleString("en-US")} ر.س`;

// الرقم التالي = أكبر رقم مستخدم في السنة + 1 (لا يتكرر بعد الحذف — BUG-032/071)
export const nextCode = (codes, prefix, year = new Date().getFullYear()) => {
  const re = new RegExp(`^${prefix}-${year}-(\\d+)$`);
  const max = codes.reduce((m, c) => { const x = String(c || "").match(re); return x ? Math.max(m, Number(x[1])) : m; }, 0);
  return `${prefix}-${year}-${String(max + 1).padStart(3, "0")}`;
};

export const TransactionsTab = ({ moduleKey = "general", moduleName = "", budget: fallbackBudget = 0 }) => {
  const txApi = useSupaTable("module_transactions", { scope: { module: moduleKey } });
  const { rows: items, insert } = txApi;
  // الميزانية من جدول ميزانيات الإدارات (المالية ← الميزانيات)؛ الرقم الثابت احتياطي لو لم تُخصص (BUG-032)
  const { rows: budgets } = useSupaTable("finance_budgets");
  const year = new Date().getFullYear();
  const dbBudget = budgets.filter(b => Number(b.year) === year && normalizeDept(b.department) === deptNameOf(moduleKey))
    .reduce((s, b) => s + (Number(b.allocated) || 0), 0);
  const budget = dbBudget || fallbackBudget;
  const canChangeStatus = ["finance", "accountant", "exec", "system"].includes(currentRole());
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(false);
  const [details, setDetails] = useState(null);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [toast, setToast] = useState("");
  const today = todayISO();
  const [form, setForm] = useState({
    date: today, type: "مصروف نثري", description: "", beneficiary: "",
    amount: "", paymentMethod: "تحويل بنكي", invoiceNo: "",
    status: "pending", attachment: "", notes: "",
  });

  // المصروف = المدفوع فقط (مثل المالية ولوحة التحكم)، والمعلق يُعرض منفصلاً
  const totalSpent = items.filter(t => t.status === "paid").reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const totalPending = items.filter(t => t.status === "pending" || t.status === "approval").reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const remaining = budget - totalSpent;
  const remainingColor = remaining >= 0 ? "#2D8B59" : "#C4513D";

  const filtered = useMemo(() => items.filter(t =>
    (!search ||
      t.description?.toLowerCase().includes(search.toLowerCase()) ||
      t.code?.toLowerCase().includes(search.toLowerCase()) ||
      t.beneficiary?.toLowerCase().includes(search.toLowerCase()) ||
      t.invoiceNo?.toLowerCase().includes(search.toLowerCase())
    ) &&
    (!filterType || t.type === filterType) &&
    (!filterStatus || t.status === filterStatus)
  ), [items, search, filterType, filterStatus]);

  const save = async () => {
    if (!form.description || !form.amount) { setToast("الرجاء إدخال الوصف والمبلغ"); return; }
    if (!(Number(form.amount) > 0)) { setToast("المبلغ يجب أن يكون أكبر من صفر"); return; }
    const code = nextCode(items.map(t => t.code), "FIN");
    setSaving(true);
    const saved = await insert({ code, ...form, amount: Number(form.amount), attachment: form.attachment || null });
    setSaving(false);
    if (!saved) return;
    setModal(false);
    setForm({
      date: today, type: "مصروف نثري", description: "", beneficiary: "",
      amount: "", paymentMethod: "تحويل بنكي", invoiceNo: "",
      status: "pending", attachment: "", notes: "",
    });
    setToast("تم حفظ المعاملة المالية بنجاح");
  };

  const columns = [
    { key: "code", label: "الرقم", render: r => <span style={{ fontWeight: 700, color: NAVY, fontSize: 11 }}>{r.code}</span> },
    { key: "date", label: "التاريخ", render: r => <span style={{ fontSize: 11 }}>{fmtDate(r.date)}</span> },
    { key: "type", label: "النوع", render: r => <Badge color={TEAL}>{r.type}</Badge> },
    { key: "description", label: "الوصف", render: r => (
      <div>
        <div style={{ fontWeight: 600, color: C.text, fontSize: 12 }}>{r.description}</div>
        {r.beneficiary && <div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>المستفيد: {r.beneficiary}</div>}
      </div>
    )},
    { key: "amount", label: "المبلغ", render: r => (
      <span style={{ fontWeight: 700, color: NAVY }}>{fmtSAR(r.amount)}</span>
    ), csvValue: r => r.amount },
    { key: "paymentMethod", label: "الدفع", render: r => <span style={{ fontSize: 11, color: C.textLight }}>{r.paymentMethod}</span> },
    { key: "invoiceNo", label: "رقم الفاتورة", render: r => <span style={{ fontSize: 11, fontFamily: "monospace", color: C.textLight }}>{r.invoiceNo || "—"}</span> },
    { key: "status", label: "الحالة", render: r => {
      const st = TX_STATUSES[r.status] || TX_STATUSES.pending;
      if (!canChangeStatus) return <Badge color={st.color}>{st.icon} {st.name}</Badge>;
      // تغيير حالة المعاملة (معلق ← مدفوع...) بصلاحية المالية
      return (
        <select value={r.status || "pending"} onClick={(e) => e.stopPropagation()}
          onChange={async (e) => { if (await txApi.update(r.id, { status: e.target.value })) setToast("تم تحديث حالة المعاملة"); }}
          style={{ padding: "3px 8px", borderRadius: 12, fontSize: 10, fontWeight: 600, border: `1px solid ${st.color}40`, background: `${st.color}15`, color: st.color, fontFamily: "inherit", cursor: "pointer" }}>
          {Object.entries(TX_STATUSES).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
        </select>
      );
    }, csvValue: r => TX_STATUSES[r.status]?.name },
    { key: "attachment", label: "مرفق", render: r => {
      if (!r.attachment) return <span style={{ color: C.textLight }}>—</span>;
      const att = typeof r.attachment === "string" ? { path: r.attachment, name: r.attachment } : r.attachment;
      return (
        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openFile(att.path); }} title={att.name}
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", color: TEAL, display: "inline-flex", alignItems: "center", gap: 3, textDecoration: "none" }}>
          <Paperclip size={13} />
        </button>
      );
    }},
    { key: "actions", label: "", render: r => (
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button onClick={() => setDetails(r)} style={{ background: "transparent", border: "none", cursor: "pointer", color: NAVY, padding: 4 }}>
          <Eye size={14} />
        </button>
        <DeleteBtn what={`${r.code} — ${r.description}`} onDelete={() => txApi.remove(r.id)} />
      </div>
    )},
  ];

  return (
    <div>
      {/* KPIs */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 10, marginBottom: 16,
      }}>
        <KPI label="الميزانية المخصصة" value={budget > 0 ? fmtSAR(budget) : "غير محددة"}
          color={NAVY} icon={<Wallet size={16} />} />
        <KPI label="إجمالي المصروفات (المدفوع)" value={fmtSAR(totalSpent)} color="#C4513D" icon={<TrendingDown size={16} />}
          sub={totalPending ? `+ ${fmtSAR(totalPending)} معلق` : ""} />
        <KPI label="المتبقي" value={budget > 0 ? fmtSAR(remaining) : "—"}
          color={remainingColor} icon={<TrendingUp size={16} />}
          sub={budget > 0 ? `${Math.round((Math.abs(remaining) / budget) * 100)}% من الميزانية` : ""} />
        <KPI label="عدد المعاملات" value={items.length} color={GOLD} icon={<Receipt size={16} />} />
      </div>

      {/* Filters */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 8, flex: 1, minWidth: 240 }}>
          <SearchBar value={search} onChange={setSearch} placeholder="ابحث برقم المعاملة، الوصف، أو رقم الفاتورة..." />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{ padding: "8px 12px", borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 12, fontFamily: "inherit", background: "#fff", cursor: "pointer" }}>
          <option value="">كل الأنواع</option>
          {TX_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: "8px 12px", borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 12, fontFamily: "inherit", background: "#fff", cursor: "pointer" }}>
          <option value="">كل الحالات</option>
          {Object.entries(TX_STATUSES).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
        </select>
        <Btn variant="accent" onClick={() => setModal(true)}><Plus size={14} /> معاملة مالية جديدة</Btn>
      </div>

      <Card padding={0}>
        <DataTable columns={columns} data={filtered} emptyMsg="لا توجد معاملات مالية مطابقة" />
      </Card>

      {/* Add Modal */}
      <Modal open={modal} onClose={() => setModal(false)} size="lg"
        title={`معاملة مالية جديدة${moduleName ? " — " + moduleName : ""}`}
        footer={<FormActions onCancel={() => setModal(false)} onSave={save} saving={saving} saveLabel="حفظ المعاملة" />}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="تاريخ المعاملة" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
          <Field label="نوع المعاملة" type="select" required value={form.type} onChange={v => setForm({ ...form, type: v })}
            options={TX_TYPES} />
        </div>
        <Field label="الوصف / البيان" required value={form.description} onChange={v => setForm({ ...form, description: v })}
          placeholder="مثال: شراء مواد دعوية للجالية الهندية" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="المستفيد / المورد" value={form.beneficiary} onChange={v => setForm({ ...form, beneficiary: v })}
            placeholder="اسم الشركة أو الشخص" />
          <Field label="المبلغ (ر.س)" type="number" required value={form.amount} onChange={v => setForm({ ...form, amount: v })} placeholder="0" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="طريقة الدفع" type="select" value={form.paymentMethod} onChange={v => setForm({ ...form, paymentMethod: v })}
            options={TX_PAYMENT_METHODS} />
          <Field label="رقم الفاتورة / المستند" value={form.invoiceNo} onChange={v => setForm({ ...form, invoiceNo: v })}
            placeholder="INV-2026-xxxx" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })}
            options={Object.entries(TX_STATUSES).map(([k, v]) => ({ value: k, label: v.name }))} />
          <FileUploadField
            label="مرفق الفاتورة/الإيصال"
            value={form.attachment}
            onChange={v => setForm({ ...form, attachment: v })}
            folder="invoices"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            maxSizeMB={10}
          />
        </div>
        <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
      </Modal>

      {/* Details Modal */}
      <Modal open={!!details} onClose={() => setDetails(null)} size="md"
        title={details ? `${details.code} — ${details.description}` : ""}
        footer={<Btn variant="ghost" onClick={() => setDetails(null)}>إغلاق</Btn>}>
        {details && (
          <div style={{ display: "grid", gap: 8, fontSize: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div><strong>التاريخ:</strong> {fmtDate(details.date)}</div>
              <div><strong>النوع:</strong> <Badge color={TEAL}>{details.type}</Badge></div>
              <div><strong>المستفيد/المورد:</strong> {details.beneficiary || "—"}</div>
              <div><strong>المبلغ:</strong> <span style={{ fontWeight: 700, color: NAVY }}>{fmtSAR(details.amount)}</span></div>
              <div><strong>طريقة الدفع:</strong> {details.paymentMethod}</div>
              <div><strong>رقم الفاتورة:</strong> <span style={{ fontFamily: "monospace" }}>{details.invoiceNo || "—"}</span></div>
              <div><strong>الحالة:</strong>{" "}
                <Badge color={(TX_STATUSES[details.status] || TX_STATUSES.pending).color}>
                  {(TX_STATUSES[details.status] || TX_STATUSES.pending).icon} {(TX_STATUSES[details.status] || TX_STATUSES.pending).name}
                </Badge>
              </div>
              <div><strong>المرفق:</strong>{" "}
                {(() => {
                  if (!details.attachment) return "—";
                  const att = typeof details.attachment === "string" ? { path: details.attachment, name: details.attachment } : details.attachment;
                  return (
                    <button type="button" onClick={(e) => { e.preventDefault(); openFile(att.path); }}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4, color: TEAL, textDecoration: "none", fontWeight: 600 }}>
                      <Paperclip size={11} /> {att.name} <ExternalLink size={10} />
                    </button>
                  );
                })()}
              </div>
            </div>
            {details.notes && (
              <div style={{ padding: 10, background: "#f8f9fb", borderRadius: 8, marginTop: 6 }}>
                <strong>ملاحظات:</strong> {details.notes}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Toast msg={toast} onClose={() => setToast("")} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 17. EventWorkflow
// ═══════════════════════════════════════════════════════════
// أيقونة Lucide لكل مرحلة بدل الإيموجي المحذوف (BUG-064)
const STAGE_ICONS = [ListChecks, Mic, ImageIcon, CheckCircle, FileText];
export const EventWorkflow = ({ currentStage = 1, onStageClick }) => (
  <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
    {EVENT_STAGES.map((stage, i) => {
      const done = i + 1 <= currentStage;
      const StageIcon = STAGE_ICONS[i] || CheckCircle;
      return (
        <div key={stage.id} onClick={() => onStageClick?.(i + 1)} style={{
          flex: 1, textAlign: "center", padding: "10px 6px", borderRadius: 10,
          background: done ? `${NAVY}10` : "#f8f9fb", cursor: onStageClick ? "pointer" : "default",
          transition: "all 0.2s",
        }}>
          <div style={{ marginBottom: 3, color: done ? NAVY : C.textLight }}><StageIcon size={16} /></div>
          <div style={{ fontSize: 10, fontWeight: done ? 700 : 400, color: done ? NAVY : C.textLight }}>{stage.name}</div>
        </div>
      );
    })}
  </div>
);

// ═══════════════════════════════════════════════════════════
// 18. ColorTemplate
// ═══════════════════════════════════════════════════════════
export const ColorTemplate = ({ colorIndex = 0, eventType = "درس علمي", title, sheikh, mosque, date, time }) => {
  const tc = TEMPLATE_COLORS[colorIndex % TEMPLATE_COLORS.length];
  return (
    <div style={{
      background: `linear-gradient(135deg, ${tc.hex} 0%, ${tc.hex}dd 100%)`,
      color: "#fff", borderRadius: 14, padding: 20, position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: -20, insetInlineEnd: -20, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
      <div style={{ position: "relative", zIndex: 1 }}>
        <Badge color="#ffffff">{eventType}</Badge>
        <h3 style={{ margin: "10px 0 6px", fontSize: 16, fontWeight: 700 }}>{title || "عنوان الحدث"}</h3>
        {sheikh && <div style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}><Mic size={11} /> {sheikh}</div>}
        {mosque && <div style={{ fontSize: 12, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}><Building2 size={11} /> {mosque}</div>}
        <div style={{ fontSize: 11, marginTop: 6 }}>
          {date && `${fmtDate(date)}`} {time && `| ${time}`}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 19. EmptyState
// ═══════════════════════════════════════════════════════════
export const EmptyState = ({ icon, title, desc, action }) => (
  <div style={{ textAlign: "center", padding: "56px 20px" }}>
    {icon && <div style={{ fontSize: 48, marginBottom: 14, opacity: 0.5 }}>{icon}</div>}
    <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>{title}</h3>
    {desc && <p style={{ fontSize: 13, color: C.textLight, marginBottom: 16 }}>{desc}</p>}
    {action}
  </div>
);

// ═══════════════════════════════════════════════════════════
// 20. SearchBar — مع تأثير focus ذهبي
// ═══════════════════════════════════════════════════════════
export const SearchBar = ({ value, onChange, placeholder = "بحث..." }) => (
  <div style={{ position: "relative", flex: 1, minWidth: 180 }}>
    <Search size={15} style={{
      position: "absolute", insetInlineStart: 12, top: "50%", transform: "translateY(-50%)", color: C.textLight,
    }} />
    <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      onFocus={(e) => { e.target.style.background = "#fff"; e.target.style.boxShadow = `0 0 0 3px ${GOLD}25`; }}
      onBlur={(e) => { e.target.style.background = "#f0f2f5"; e.target.style.boxShadow = "none"; }}
      style={{
        width: "100%", paddingBlock: 9, paddingInlineStart: 38, paddingInlineEnd: 14, border: "none",
        borderRadius: 10, fontSize: 12, fontFamily: "inherit",
        background: "#f0f2f5", color: C.text, outline: "none",
        transition: "all 0.2s",
      }} />
  </div>
);

// ═══════════════════════════════════════════════════════════
// 21. ModuleHeader
// ═══════════════════════════════════════════════════════════
export const ModuleHeader = ({ icon, title, subtitle, actions }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    marginBottom: 18, gap: 12,
  }}>
    <div>
      <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 24 }}>{icon}</span> {title}
      </h1>
      {subtitle && <p style={{ margin: "4px 0 0", fontSize: 12, color: C.textLight }}>{subtitle}</p>}
    </div>
    {actions && <div style={{ display: "flex", gap: 8 }}>{actions}</div>}
  </div>
);

// ═══════════════════════════════════════════════════════════
// 22. DigitalStamp — الختم الإلكتروني للمدير التنفيذي (الجزء 7)
// ═══════════════════════════════════════════════════════════
export const DigitalStamp = ({ name = "المدير التنفيذي", date, serialNo, role = "exec" }) => {
  if (role !== "exec" && role !== "system") return null;
  const d = date ? fmtDate(date) : fmtDate(new Date());
  const serial = serialNo || `STM-${Date.now().toString().slice(-6)}`;
  return (
    <div style={{
      width: 120, height: 120, borderRadius: "50%",
      border: `3px double ${NAVY}`,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: 4, textAlign: "center", background: "#fff",
      boxShadow: "0 2px 8px rgba(0,61,88,0.15)",
      position: "relative",
    }}>
      <Stamp size={14} style={{ color: NAVY, marginBottom: 2 }} />
      <div style={{ fontSize: 7, fontWeight: 700, color: NAVY, lineHeight: 1.2 }}>
        {ORG.shortName}
      </div>
      <div style={{ fontSize: 6, color: NAVY, marginTop: 2 }}>{name}</div>
      <div style={{ fontSize: 6, color: C.textLight, marginTop: 2 }}>{d}</div>
      <div style={{ fontSize: 5, color: C.textLight, marginTop: 1 }}>{serial}</div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 23. QuickAction — للداشبورد مع hover
// ═══════════════════════════════════════════════════════════
export const QuickAction = ({ icon, label, color = NAVY, onClick }) => {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: "#fff", border: `1.5px solid ${hover ? color : C.border}`,
        borderRadius: 12, padding: 14, cursor: "pointer", fontFamily: "inherit",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
        transition: "all 0.25s ease",
        boxShadow: hover ? `0 6px 20px ${color}25` : "none",
        transform: hover ? "translateY(-2px)" : "translateY(0)",
      }}>
      <div style={{
        width: 40, height: 40, borderRadius: "50%",
        background: `linear-gradient(135deg, ${color}20, ${color}08)`,
        color, display: "flex", alignItems: "center", justifyContent: "center",
      }}>{icon}</div>
      <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{label}</span>
    </button>
  );
};

// ═══════════════════════════════════════════════════════════
// 24. ExcelImport — استيراد بيانات من Excel/CSV مع مطابقة أعمدة
// ═══════════════════════════════════════════════════════════
const normalizeHeader = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[\u064B-\u0652\u0670\u0640]/g, "")
    .replace(/[إأآا]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/[\s\-_،,().:/\\]/g, "")
    .trim();

const autoMatch = (header, fields) => {
  const nh = normalizeHeader(header);
  if (!nh) return "";
  for (const f of fields) {
    const candidates = [f.key, f.label, ...(f.aliases || [])].map(normalizeHeader);
    if (candidates.includes(nh)) return f.key;
  }
  for (const f of fields) {
    const candidates = [f.key, f.label, ...(f.aliases || [])].map(normalizeHeader);
    if (candidates.some((c) => c && (c.includes(nh) || nh.includes(c)))) return f.key;
  }
  return "";
};

export const ExcelImport = ({ open, onClose, title = "استيراد من Excel", fields = [], onImport, templateName = "import-template" }) => {
  const [step, setStep] = useState(1);
  const [fileName, setFileName] = useState("");
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [dateOrder, setDateOrder] = useState("dmy"); // صيغة التواريخ في الملف (BUG-029)
  const inputRef = useRef(null);

  const reset = useCallback(() => {
    setStep(1);
    setFileName("");
    setRawHeaders([]);
    setRawRows([]);
    setMapping({});
    setError("");
    setWarning("");
  }, []);

  useEffect(() => { if (!open) reset(); }, [open, reset]);

  const parseFile = useCallback((file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!/\.(xlsx|xls|csv)$/.test(name)) {
      setError("صيغة الملف غير مدعومة. يُرجى رفع xlsx أو xls أو csv");
      return;
    }
    setError("");
    setFileName(file.name);
    const reader = new FileReader();
    reader.onerror = () => setError("تعذّر قراءة الملف");
    reader.onload = async (e) => {
      try {
        const XLSX = await import("xlsx");
        const data = new Uint8Array(e.target.result);
        let wb;
        if (name.endsWith(".csv")) {
          // CSV: فك الترميز يدوياً — UTF-8 (بـ BOM أو بدونه) وإلا Windows-1256 للملفات العربية القديمة (BUG-028)
          let text;
          try { text = new TextDecoder("utf-8", { fatal: true }).decode(data); }
          catch { text = new TextDecoder("windows-1256").decode(data); }
          text = text.replace(/^\uFEFF/, "");
          if (text.includes("\uFFFD")) setWarning("بعض الحروف في الملف غير مقروءة — تأكد أن الملف محفوظ بترميز UTF-8");
          // raw: true = النص كما هو (بدون تحويل التواريخ لصيغة أمريكية)
          wb = XLSX.read(text, { type: "string", raw: true });
        } else {
          wb = XLSX.read(data, { type: "array" });
        }
        const sheet = wb.Sheets[wb.SheetNames[0]];
        // خلايا التاريخ الفعلية في Excel تطلع بصيغة ISO مباشرة (BUG-029)
        const arr = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false, dateNF: "yyyy-mm-dd" });
        if (!arr.length) { setError("الملف فارغ"); return; }
        const headers = arr[0].map((h) => String(h || "").trim());
        const rows = arr.slice(1).filter((r) => r.some((c) => String(c || "").trim() !== ""));
        if (!rows.length) { setError("لا توجد بيانات بعد صف العناوين"); return; }
        const initialMap = {};
        headers.forEach((h, i) => {
          const matched = autoMatch(h, fields);
          if (matched && !Object.values(initialMap).includes(matched)) {
            initialMap[i] = matched;
          }
        });
        setRawHeaders(headers);
        setRawRows(rows);
        setMapping(initialMap);
        setStep(2);
      } catch (err) {
        setError("تعذّر تحليل الملف: " + (err.message || "خطأ غير معروف"));
      }
    };
    reader.readAsArrayBuffer(file);
  }, [fields]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    parseFile(file);
  };

  const downloadTemplate = () => {
    const header = fields.map((f) => f.label).join(",");
    const sample = fields.map((f) => f.sample || "").join(",");
    const bom = "\uFEFF";
    const blob = new Blob([bom + header + "\n" + sample + "\n"], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${templateName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const mappedFieldKeys = useMemo(() => new Set(Object.values(mapping).filter(Boolean)), [mapping]);
  const missingRequired = useMemo(
    () => fields.filter((f) => f.required && !mappedFieldKeys.has(f.key)),
    [fields, mappedFieldKeys]
  );

  const buildRows = useCallback(() => {
    return rawRows.map((row) => {
      const out = {};
      Object.entries(mapping).forEach(([colIdx, fieldKey]) => {
        if (!fieldKey) return;
        const val = row[Number(colIdx)];
        const field = fields.find((f) => f.key === fieldKey);
        let v = val === undefined || val === null ? "" : String(val).trim();
        // تاريخ نصي يوم/شهر/سنة أو شهر/يوم/سنة → YYYY-MM-DD حسب الصيغة المختارة (BUG-029)
        const dm = v.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
        if (dm) {
          const [day, month] = dateOrder === "mdy" ? [dm[2], dm[1]] : [dm[1], dm[2]];
          const year = dm[3].length === 2 ? `20${dm[3]}` : dm[3];
          v = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        }
        if (field?.type === "number") {
          const n = Number(String(v).replace(/[٬,\s]/g, ""));
          v = isNaN(n) ? 0 : n;
        } else if (field?.type === "boolean") {
          v = ["true", "1", "نعم", "yes", "y", "صح", "✓"].includes(String(v).toLowerCase());
        }
        if (field?.transform) v = field.transform(v);
        out[fieldKey] = v;
      });
      return out;
    });
  }, [rawRows, mapping, fields, dateOrder]);

  const confirmImport = () => {
    if (missingRequired.length) {
      setError("الرجاء مطابقة الحقول المطلوبة: " + missingRequired.map((f) => f.label).join("، "));
      return;
    }
    const rows = buildRows();
    onImport?.(rows);
    onClose?.();
  };

  if (!open) return null;

  const previewRows = rawRows.slice(0, 10);
  const previewFooter = (
    <>
      {step === 1 && (
        <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
      )}
      {step === 2 && (
        <>
          <Btn onClick={() => setStep(3)} disabled={missingRequired.length > 0}>
            متابعة المعاينة
          </Btn>
          <Btn variant="ghost" onClick={reset}>إعادة رفع</Btn>
        </>
      )}
      {step === 3 && (
        <>
          <Btn variant="teal" onClick={confirmImport}>
            <CheckCircle size={14} /> تأكيد الاستيراد ({rawRows.length} صفًا)
          </Btn>
          <Btn variant="ghost" onClick={() => setStep(2)}>رجوع للمطابقة</Btn>
        </>
      )}
    </>
  );

  return (
    <Modal open={open} onClose={onClose} title={title} size="xl" footer={previewFooter}>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, fontSize: 11, color: C.textLight }}>
        {[
          { n: 1, label: "رفع الملف" },
          { n: 2, label: "مطابقة الأعمدة" },
          { n: 3, label: "المعاينة والتأكيد" },
        ].map((s, i, arr) => (
          <React.Fragment key={s.n}>
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              fontWeight: step === s.n ? 700 : 500,
              color: step >= s.n ? NAVY : C.textLight,
            }}>
              <span style={{
                width: 22, height: 22, borderRadius: "50%",
                background: step >= s.n ? `linear-gradient(135deg, ${NAVY}, ${TEAL})` : "#e9ecef",
                color: step >= s.n ? "#fff" : C.textLight,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700,
              }}>{step > s.n ? <CheckCircle size={12} /> : s.n}</span>
              {s.label}
            </div>
            {i < arr.length - 1 && <div style={{ flex: 1, height: 1, background: C.border, alignSelf: "center" }} />}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div style={{
          background: "#fdecea", color: C.danger, padding: "10px 14px",
          borderRadius: 10, fontSize: 12, marginBottom: 14,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {step === 1 && (
        <div>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? GOLD : C.border}`,
              background: dragOver ? `${GOLD}10` : "#f8f9fb",
              borderRadius: 14, padding: "40px 20px",
              textAlign: "center", cursor: "pointer",
              transition: "all 0.2s ease",
            }}>
            <div style={{
              width: 56, height: 56, margin: "0 auto 12px",
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff",
            }}>
              <Upload size={26} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: NAVY, marginBottom: 4 }}>
              اسحب الملف هنا أو انقر للاختيار
            </div>
            <div style={{ fontSize: 11, color: C.textLight }}>
              صيغ مدعومة: xlsx, xls, csv
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
              onChange={(e) => parseFile(e.target.files?.[0])}
            />
          </div>

          {fields.length > 0 && (
            <div style={{ marginTop: 16, padding: 14, background: "#f8f9fb", borderRadius: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: NAVY }}>
                  الأعمدة المتوقّعة
                </div>
                <Btn variant="outline" size="sm" onClick={downloadTemplate}>
                  <Download size={12} /> تنزيل قالب
                </Btn>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {fields.map((f) => (
                  <Badge key={f.key} color={f.required ? NAVY : C.textLight}>
                    {f.label}{f.required ? " *" : ""}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {warning && (
        <div style={{ background: `${GOLD}18`, color: NAVY, padding: "10px 14px", borderRadius: 10, fontSize: 12, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
          <AlertCircle size={14} /> {warning}
        </div>
      )}

      {step === 2 && (
        <div>
          <div style={{ fontSize: 12, color: C.textLight, marginBottom: 12 }}>
            الملف: <strong style={{ color: NAVY }}>{fileName}</strong> — {rawRows.length} صفًا، {rawHeaders.length} عمودًا
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 12, color: C.text }}>
            <Calendar size={13} style={{ color: TEAL }} /> صيغة التواريخ في الملف:
            <select value={dateOrder} onChange={(e) => setDateOrder(e.target.value)}
              style={{ padding: "5px 8px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 12, fontFamily: "inherit", background: "#fff" }}>
              <option value="dmy">يوم/شهر/سنة (مثال 05/03/2026 = 5 مارس)</option>
              <option value="mdy">شهر/يوم/سنة (مثال 03/05/2026 = 5 مارس)</option>
            </select>
          </div>

          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10,
            padding: "10px 12px", background: "#f0f2f5", borderRadius: 10,
            fontSize: 11, fontWeight: 700, color: NAVY, marginBottom: 6,
          }}>
            <div>عمود الملف</div>
            <div>عيّنة</div>
            <div>الحقل في النظام</div>
          </div>

          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {rawHeaders.map((h, i) => {
              const sample = previewRows.find((r) => String(r[i] || "").trim() !== "")?.[i] || "";
              const current = mapping[i] || "";
              return (
                <div key={i} style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 10, padding: "8px 12px",
                  borderBottom: `1px solid ${C.border}`,
                  fontSize: 12, alignItems: "center",
                }}>
                  <div style={{ fontWeight: 600, color: C.text }}>{h || `(عمود ${i + 1})`}</div>
                  <div style={{ color: C.textLight, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {String(sample).slice(0, 40)}
                  </div>
                  <select
                    value={current}
                    onChange={(e) => setMapping({ ...mapping, [i]: e.target.value })}
                    style={{
                      padding: "6px 8px", border: `1.5px solid ${current ? TEAL : C.border}`,
                      borderRadius: 8, fontSize: 12, fontFamily: "inherit",
                      background: "#fff", color: C.text, cursor: "pointer",
                    }}>
                    <option value="">— تجاهل العمود —</option>
                    {fields.map((f) => (
                      <option key={f.key} value={f.key}
                        disabled={mappedFieldKeys.has(f.key) && current !== f.key}>
                        {f.label}{f.required ? " *" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>

          {missingRequired.length > 0 && (
            <div style={{
              marginTop: 12, padding: "8px 12px",
              background: `${GOLD}18`, color: NAVY, borderRadius: 8,
              fontSize: 11, display: "flex", alignItems: "center", gap: 6,
            }}>
              <AlertCircle size={12} />
              يجب مطابقة: {missingRequired.map((f) => f.label).join("، ")}
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div>
          <div style={{ fontSize: 12, color: C.textLight, marginBottom: 10 }}>
            معاينة أول {Math.min(10, rawRows.length)} صف من إجمالي <strong style={{ color: NAVY }}>{rawRows.length}</strong> صفًا
          </div>
          <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr>
                  {fields.filter((f) => mappedFieldKeys.has(f.key)).map((f) => (
                    <th key={f.key} style={{
                      padding: "8px 10px", textAlign: "right",
                      background: "#f8f9fb", color: NAVY, fontWeight: 700,
                      borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
                    }}>{f.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {buildRows().slice(0, 10).map((row, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                    {fields.filter((f) => mappedFieldKeys.has(f.key)).map((f) => (
                      <td key={f.key} style={{ padding: "7px 10px", color: C.text }}>
                        {String(row[f.key] ?? "").slice(0, 60)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ═══════════════════════════════════════════════════════════
// 25. ExcelImportBtn — زر جاهز للاستخدام في الموديولات
// ═══════════════════════════════════════════════════════════
export const ExcelImportBtn = ({ title, fields, onImport, templateName, label = "استيراد Excel", variant = "outline", size = "md" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Btn variant={variant} size={size} onClick={() => setOpen(true)}>
        <FileSpreadsheet size={14} /> {label}
      </Btn>
      <ExcelImport
        open={open}
        onClose={() => setOpen(false)}
        title={title}
        fields={fields}
        onImport={onImport}
        templateName={templateName}
      />
    </>
  );
};

// ═══════════════════════════════════════════════════════════
// 26. FileUploadField — رفع فعلي للملفات على Supabase Storage
// ═══════════════════════════════════════════════════════════
const isImageMime = (m) => typeof m === "string" && m.startsWith("image/");
const isImageExt = (name) => /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name || "");

// الامتداد → أنواع MIME المقبولة (نفس قائمة allowed_mime_types المقترحة للـ bucket)
const EXT_MIME = {
  ".pdf": ["application/pdf"],
  ".jpg": ["image/jpeg"], ".jpeg": ["image/jpeg"], ".png": ["image/png"],
  ".doc": ["application/msword"],
  ".docx": ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  ".xls": ["application/vnd.ms-excel"],
  ".xlsx": ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
};

const formatBytes = (b) => {
  if (!b) return "0 KB";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

// الحروف العربية تبقى في اسم الملف (BUG-067)
const safeFileName = (name) =>
  String(name || "file")
    .replace(/[^\p{L}\p{N}._-]+/gu, "_")
    .replace(/_+/g, "_")
    .slice(0, 80);

export const FileUploadField = ({
  label, value, onChange, folder = "general",
  accept, maxSizeMB = 10, required, hint,
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const current = useMemo(() => {
    if (!value) return null;
    if (typeof value === "string") {
      return { path: value, name: value.split("/").pop() || value, size: 0, type: "" };
    }
    return value;
  }, [value]);

  const acceptExts = useMemo(() => {
    if (!accept) return null;
    return accept.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
  }, [accept]);

  const validate = (file) => {
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `الحجم يتجاوز ${maxSizeMB} ميجابايت`;
    }
    if (acceptExts && acceptExts.length) {
      const name = file.name.toLowerCase();
      const ok = acceptExts.some((ext) => name.endsWith(ext));
      if (!ok) return `الصيغة غير مدعومة. المسموح: ${accept}`;
      // نوع الملف الفعلي (MIME) لازم يطابق الامتداد — مش الامتداد بس (BUG-009)
      const mimes = acceptExts.flatMap((ext) => EXT_MIME[ext] || []);
      if (file.type && mimes.length && !mimes.includes(file.type)) return `نوع الملف لا يطابق امتداده. المسموح: ${accept}`;
    }
    return "";
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    const msg = validate(file);
    if (msg) { setError(msg); return; }

    setUploading(true);
    try {
      const stamp = Date.now();
      const path = `${folder}/${stamp}_${safeFileName(file.name)}`;
      await uploadFile(path, file);
      onChange?.({
        path,
        name: file.name,
        size: file.size,
        type: file.type || "",
      });
    } catch (err) {
      setError(err.message || "تعذّر الرفع");
    } finally {
      setUploading(false);
    }
  };

  // إزالة المرفق من النموذج فقط — الملف نفسه لا يُحذف من التخزين هنا، لأن السجل المحفوظ
  // قد يظل يشير إليه لو أُلغي التعديل (BUG-049)
  const handleRemove = () => {
    setError("");
    onChange?.(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const showImagePreview = current && (isImageMime(current.type) || isImageExt(current.name) || isImageExt(current.path));
  // المرفقات خاصة (BUG-009): معاينة الصورة برابط موقّع مؤقت، والفتح برابط موقّع جديد عند الضغط
  const [previewUrl, setPreviewUrl] = useState("");
  const previewPath = showImagePreview ? current.path : "";
  useEffect(() => {
    setPreviewUrl("");
    if (!previewPath) return;
    let alive = true;
    getSignedUrl(previewPath).then((u) => { if (alive) setPreviewUrl(u); }).catch(() => {});
    return () => { alive = false; };
  }, [previewPath]);
  const openCurrent = (e) => { e.preventDefault(); openFile(current.path); };

  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.textLight, marginBottom: 6 }}>
          {label} {required && <span style={{ color: C.danger }}>*</span>}
        </label>
      )}

      {!current ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          style={{
            border: `1.5px dashed ${dragOver ? GOLD : C.border}`,
            background: dragOver ? `${GOLD}10` : "#fafbfc",
            borderRadius: 10, padding: "16px 14px",
            textAlign: "center", cursor: uploading ? "wait" : "pointer",
            transition: "all 0.2s ease",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          }}>
          {uploading ? (
            <>
              <Loader size={16} style={{ color: TEAL, animation: "spin 1s linear infinite" }} />
              <span style={{ fontSize: 12, color: C.textLight }}>جاري الرفع...</span>
            </>
          ) : (
            <>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
                display: "flex", alignItems: "center", justifyContent: "center", color: "#fff",
              }}>
                <Upload size={16} />
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: NAVY }}>
                  اسحب الملف هنا أو انقر للاختيار
                </div>
                <div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>
                  {accept ? `الصيغ: ${accept}` : "كل الصيغ"} — الحد الأقصى {maxSizeMB} ميجابايت
                </div>
              </div>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      ) : (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 12px", border: `1.5px solid ${TEAL}40`,
          background: `${TEAL}08`, borderRadius: 10,
        }}>
          {showImagePreview ? (
            <button type="button" onClick={openCurrent}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", display: "block", flexShrink: 0 }}>
              <img src={previewUrl || undefined} alt={current.name}
                style={{
                  width: 44, height: 44, borderRadius: 8, objectFit: "cover",
                  border: `1px solid ${C.border}`,
                }} />
            </button>
          ) : (
            <div style={{
              width: 38, height: 38, borderRadius: 8,
              background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
              color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <FileText size={18} />
            </div>
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 12, fontWeight: 600, color: C.text,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{current.name}</div>
            <div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>
              {current.size ? formatBytes(current.size) : "مرفوع"}
            </div>
          </div>

          <button type="button" onClick={openCurrent}
            title="فتح في تبويب جديد"
            style={{ cursor: "pointer", fontFamily: "inherit",
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "6px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600,
              background: "#fff", color: NAVY, border: `1px solid ${C.border}`,
              textDecoration: "none",
            }}>
            <ExternalLink size={12} /> فتح
          </button>

          <button
            onClick={handleRemove}
            disabled={uploading}
            title="حذف الملف"
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 30, height: 30, borderRadius: 8, border: "none",
              background: `${C.danger}15`, color: C.danger,
              cursor: uploading ? "wait" : "pointer",
            }}>
            {uploading ? <Loader size={14} style={{ animation: "spin 1s linear infinite" }} /> : <Trash2 size={14} />}
          </button>
        </div>
      )}

      {error && (
        <div style={{
          marginTop: 6, fontSize: 11, color: C.danger,
          display: "flex", alignItems: "center", gap: 4,
        }}>
          <AlertCircle size={12} /> {error}
        </div>
      )}

      {hint && !error && (
        <div style={{ fontSize: 10, color: C.textLight, marginTop: 4 }}>{hint}</div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 27. RecordFormModal / AddRecordBtn — نموذج إضافة عام يحفظ في Supabase
// fields: [{ key, label, type: text|textarea|number|date|select|bool|list, options, required }]
// ═══════════════════════════════════════════════════════════
const emptyValue = (f) => (f.type === "bool" ? false : f.type === "list" ? "" : "");

// تحقق حسب نوع الحقل — يرجع رسالة خطأ أو "" (BUG-042)
const validateField = (f, v) => {
  if (v === undefined || v === null || String(v).trim() === "") return "";
  if (f.type === "number") {
    const n = Number(v);
    if (isNaN(n)) return `${f.label}: رقم غير صالح`;
    if (f.min !== undefined && n < f.min) return `${f.label}: لا يقل عن ${f.min}`;
    if (f.positive && n <= 0) return `${f.label}: يجب أن يكون أكبر من صفر`;
  }
  if (f.type === "tel" && !isValidPhone(v)) return `${f.label}: رقم جوال غير صالح (مثال 05xxxxxxxx)`;
  if (f.type === "email" && !isValidEmail(v)) return `${f.label}: بريد إلكتروني غير صالح`;
  if (f.check && !f.check(v)) return `${f.label}: ${f.checkError || "قيمة غير صالحة"}`;
  return "";
};

export const RecordFormModal = ({ open, onClose, title, fields, initial = {}, onSubmit, saveLabel = "حفظ", validate }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [dirty, setDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  // إغلاق نموذج فيه تغييرات غير محفوظة يحتاج تأكيد (BUG-041)
  const requestClose = () => {
    if (dirty && !confirmClose) { setConfirmClose(true); return; }
    onClose();
  };
  const change = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); setConfirmClose(false); };
  useEffect(() => {
    if (!open) return;
    setDirty(false);
    setConfirmClose(false);
    const f = {};
    fields.forEach((x) => {
      const v = initial[x.key];
      f[x.key] = v === undefined || v === null ? (x.type === "file" ? null : emptyValue(x)) : x.type === "list" && Array.isArray(v) ? v.join("\n") : v;
    });
    setForm(f);
    setErr("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    // مسافة فاضية = قيمة فاضية (BUG-042)
    const missing = fields.filter((x) => x.required && (form[x.key] === undefined || form[x.key] === null || String(form[x.key]).trim() === ""));
    if (missing.length) { setErr("الرجاء إدخال: " + missing.map((x) => x.label).join("، ")); return; }
    const fieldErr = fields.map((x) => validateField(x, form[x.key])).find(Boolean);
    if (fieldErr) { setErr(fieldErr); return; }
    const out = {};
    fields.forEach((x) => {
      const v = form[x.key];
      if (x.type === "number") out[x.key] = v === "" ? null : Number(v);
      else if (x.type === "file") out[x.key] = v || null;
      else if (x.type === "list") out[x.key] = String(v || "").split(x.lines ? /\n/ : /\n|[,،]/).map((s) => s.trim()).filter(Boolean);
      else out[x.key] = x.normalize ? x.normalize(v) : v;
    });
    const customErr = validate?.(out);
    if (customErr) { setErr(customErr); return; }
    setSaving(true);
    const ok = await onSubmit(out);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Modal open={open} onClose={requestClose} title={title} size={fields.length > 6 ? "lg" : "md"}
      footer={<FormActions onCancel={requestClose} onSave={submit} saving={saving} saveLabel={saveLabel}
        cancelLabel={confirmClose ? "تأكيد الإلغاء" : "إلغاء"} />}>
      {confirmClose && (
        <div style={{ fontSize: 12, color: C.danger, background: `${C.danger}10`, padding: "8px 12px", borderRadius: 8, marginBottom: 12, display: "flex", gap: 6, alignItems: "center" }}>
          <AlertCircle size={13} /> لديك تغييرات غير محفوظة — اضغط «تأكيد الإلغاء» للخروج بدون حفظ
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: fields.length > 4 ? "1fr 1fr" : "1fr", gap: "0 12px" }}>
        {fields.map((x) => {
          const wide = x.type === "textarea" || x.type === "list" || x.type === "file";
          const common = { key: x.key, label: x.label, required: x.required, hint: x.hint, placeholder: x.placeholder };
          const el = x.type === "file" ? (
            <FileUploadField label={x.label} required={x.required} hint={x.hint} value={form[x.key] || null}
              onChange={(v) => change({ [x.key]: v })} folder={x.folder || "records"} accept={x.accept} maxSizeMB={x.maxSizeMB || 10} />
          ) : x.type === "bool" ? (
            <Field {...common} type="select" value={form[x.key] ? "yes" : "no"}
              onChange={(v) => change({ [x.key]: v === "yes" })}
              options={[{ value: "yes", label: "نعم" }, { value: "no", label: "لا" }]} />
          ) : (
            <Field {...common}
              type={x.type === "list" ? "textarea" : x.type || "text"}
              options={x.options} rows={x.type === "list" ? 3 : undefined}
              value={form[x.key] ?? ""} onChange={(v) => change({ [x.key]: v })}
              hint={x.type === "list" ? (x.hint || "عنصر في كل سطر") : x.hint} />
          );
          return <div key={x.key} style={wide ? { gridColumn: "1 / -1" } : undefined}>{el}</div>;
        })}
      </div>
      {err && <div style={{ fontSize: 12, color: C.danger, display: "flex", gap: 6, alignItems: "center" }}><AlertCircle size={13} /> {err}</div>}
    </Modal>
  );
};

// زرار + نموذج إضافة. api = ناتج useSupaTable
export const AddRecordBtn = ({ api, label = "إضافة", title, fields, defaults = {}, onSaved, variant = "primary", size = "md", validate }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Btn variant={variant} size={size} onClick={() => setOpen(true)}><Plus size={14} /> {label}</Btn>
      <RecordFormModal open={open} onClose={() => setOpen(false)} title={title || label} fields={fields} validate={validate}
        onSubmit={async (values) => {
          const saved = await api.insert({ ...defaults, ...values });
          if (saved) onSaved?.(saved);
          return !!saved;
        }} />
    </>
  );
};

// ═══════════════════════════════════════════════════════════
// 28. DeleteBtn — حذف مع تأكيد
// ═══════════════════════════════════════════════════════════
export const DeleteBtn = ({ onDelete, what = "هذا السجل", size = 14, title = "حذف" }) => {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!canDelete()) return null; // الداعية ومدخل البيانات بدون صلاحية حذف (BUG-007)
  const run = async () => {
    setBusy(true);
    const ok = await onDelete();
    setBusy(false);
    if (ok !== null) setConfirm(false);
  };
  return (
    <>
      <button type="button" title={title}
        onClick={(e) => { e.stopPropagation(); setConfirm(true); }}
        style={{ background: `${C.danger}12`, border: "none", borderRadius: 8, padding: 5, cursor: "pointer", color: C.danger, display: "inline-flex", alignItems: "center" }}>
        <Trash2 size={size} />
      </button>
      {confirm && (
        <span onClick={(e) => e.stopPropagation()}>
          <Modal open onClose={() => setConfirm(false)} title="تأكيد الحذف" size="sm"
            footer={<>
              <Btn variant="danger" onClick={run} disabled={busy}><Trash2 size={13} /> {busy ? "جاري الحذف..." : "حذف نهائي"}</Btn>
              <Btn variant="ghost" onClick={() => setConfirm(false)}>إلغاء</Btn>
            </>}>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7 }}>
              هل تريد حذف <strong>{what}</strong>؟ لا يمكن التراجع عن هذه العملية.
            </div>
          </Modal>
        </span>
      )}
    </>
  );
};

// عمود حذف جاهز لـ DataTable
export const deleteColumn = (api, nameOf = (r) => r.name || r.title) => ({
  key: "_delete", label: "", csvValue: () => "",
  render: (r) => <DeleteBtn what={nameOf(r) || "هذا السجل"} onDelete={() => api.remove(r.id)} />,
});

// ═══════════════════════════════════════════════════════════
const SharedAPI = {
  Modal, Field, FormActions, Btn, Toast, GlobalErrorToast, ErrorBoundary, Card, KPI, Badge, Tabs,
  DataTable, exportCSV, ProgressBar, ReportsTab, MeetingsTab,
  OperationalPlanTab, TransactionsTab, EventWorkflow, ColorTemplate, EmptyState, SearchBar,
  ModuleHeader, DigitalStamp, QuickAction,
  CHART_PALETTE, BrandTooltip, BrandLegend, BrandPie,
  ExcelImport, ExcelImportBtn, FileUploadField,
  RecordFormModal, AddRecordBtn, DeleteBtn, deleteColumn,
};
export default SharedAPI;
